import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * The room strip — one recessed plate carrying the room code, the invite copy
 * and the settings gear.
 *
 * It replaces THREE things in the sidebar: the `modeBar` in `Game.js`, and
 * both plates in `SidebarActions`. Settings used to appear twice on the same
 * column (a bar at the top, a button at the bottom); the code itself — the
 * thing a host reads aloud — appeared nowhere.
 *
 * It is deliberately **not** lobby-only. The code stays useful all game, and
 * with `SidebarActions` gone this is the desktop column's only Settings door;
 * its mobile twin is the HamburgerMenu `Settings` plate, unchanged.
 *
 * Copy writes the full room URL, not the code, because that is what people
 * paste — and it is built from `origin` + `/game/<code>` rather than
 * `location.href`, so it is the same link wherever it is pressed from. The
 * label flips to a jade confirmation for 2s, as the old Share plate did.
 */
const useStyles = createUseStyles({
  strip: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    background: '#1c130c',
    borderRadius: '9px',
    boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5), inset 0 0 0 1px #402a1a',
  },
  text: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' },
  meta: {
    fontFamily: MONO,
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    // 3.9:1 on #1c130c. Do not darken to #6b5438 — that is 2.5:1 and the line
    // disappears.
    color: '#8a6e4c',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaOk: { color: '#2f9e6e' },
  code: {
    fontFamily: MONO,
    fontSize: '14.5px',
    fontWeight: 600,
    letterSpacing: '1px',
    color: '#f5e6c8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  btn: {
    width: '38px',
    height: '38px',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    background: '#3a2718',
    border: '1px solid #6b4622',
    borderRadius: '8px',
    color: '#e8d6ae',
    cursor: 'pointer',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -2px 0 rgba(0,0,0,0.35)',
    transition: 'background .15s, color .15s',
    '&:hover': { background: '#4a3220', color: '#fff3e6' },
    '&:active': { boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)' },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
  },
  btnOk: { color: '#2f9e6e', borderColor: '#2f9e6e' },
});

function RoomStrip({ gameId, mode, access, onSettings }) {
  const classes = useStyles();
  const t = useT();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    const url = `${window.location.origin}/game/${gameId}`;
    Promise.resolve(navigator.clipboard?.writeText(url))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  const meta = copied
    ? t('game.inviteCopied')
    : t('game.roomMeta', {
      visibility: t(access?.visibility === 'password' ? 'access.password' : 'access.public'),
      mode: t(`modes.${mode ?? 'normal'}`),
    });

  return (
    <div className={classes.strip}>
      <div className={classes.text}>
        <span className={`${classes.meta}${copied ? ` ${classes.metaOk}` : ''}`}>{meta}</span>
        <span className={classes.code}>{gameId}</span>
      </div>

      <button
        type="button"
        className={`${classes.btn}${copied ? ` ${classes.btnOk}` : ''}`}
        onClick={copy}
        title={t('a11y.copyInvite')}
        aria-label={t('a11y.copyInvite')}
      >
        {copied ? (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M3 8.4l3.2 3.2L13 4.8" />
          </svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
            <rect x="5.4" y="5.4" width="8.2" height="8.2" rx="1.6" />
            <path d="M10.6 5.4V3.9a1.5 1.5 0 0 0-1.5-1.5H3.9a1.5 1.5 0 0 0-1.5 1.5v5.2a1.5 1.5 0 0 0 1.5 1.5h1.5" />
          </svg>
        )}
      </button>

      <button
        type="button"
        className={classes.btn}
        onClick={onSettings}
        title={t('a11y.roomSettings')}
        aria-label={t('a11y.roomSettings')}
      >
        {/*
          * A cog: six trapezoidal teeth standing on a solid body, around a
          * hollow hub. The obvious small-icon shortcut — a circle with eight
          * radiating strokes — is a *sun*, and reads as a brightness control.
          * The teeth have to be attached to the body for this to be a gear.
          */}
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round">
          <path d="M5.99 3.7L6.3 1.67L9.7 1.67L10.01 3.7A4.75 4.75 0 0 1 10.72 4.11L12.63 3.37L14.33 6.3L12.73 7.59A4.75 4.75 0 0 1 12.73 8.41L14.33 9.7L12.63 12.63L10.72 11.89A4.75 4.75 0 0 1 10.01 12.3L9.7 14.33L6.3 14.33L5.99 12.3A4.75 4.75 0 0 1 5.28 11.89L3.37 12.63L1.67 9.7L3.27 8.41A4.75 4.75 0 0 1 3.27 7.59L1.67 6.3L3.37 3.37L5.28 4.11A4.75 4.75 0 0 1 5.99 3.7Z" />
          <circle cx="8" cy="8" r="2.15" />
        </svg>
      </button>
    </div>
  );
}

export default RoomStrip;
